import React, { useState, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import workstationIpConflictService from "../services/workstation-ip-conflict.service";
import workstationService from "../services/workstation.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";
import WorkstationAuditModal from "./WorkstationAuditModal";
import { formatDateTimeWithZone, getUserTimezone, formatDateOnly } from "../utils/dateUtils";

const WorkstationIpConflictView = () => {
    const [loading, setLoading] = useState(false);
    const [clientId, setClientId] = useState("");
    const [clientName, setClientName] = useState("");
    const [conflictDate, setConflictDate] = useState("");
    const [months, setMonths] = useState(null);
    const [workstations, setWorkstations] = useState([]);
    const [responseCode, setResponseCode] = useState(200);
    const [actionLoading, setActionLoading] = useState(false);

    // Audit modal state
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditData, setAuditData] = useState([]);
    const [selectedWorkstation, setSelectedWorkstation] = useState("");
    const [includeNonConflicting, setIncludeNonConflicting] = useState(false);

    const { user: currentUser } = useSelector((state) => state.auth);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        // Get parameters from URL
        const clientIdFromUrl = searchParams.get("clientId");
        const clientNameFromUrl = searchParams.get("clientName");
        const conflictDateFromUrl = searchParams.get("conflictingDay");
        const monthsFromUrl = searchParams.get("months");

        if (clientIdFromUrl && (conflictDateFromUrl || monthsFromUrl)) {
            setClientId(clientIdFromUrl);
            setConflictDate(conflictDateFromUrl || "");
            setMonths(monthsFromUrl ? parseInt(monthsFromUrl) : null);
            if (clientNameFromUrl) {
                setClientName(clientNameFromUrl);
            }
            setResponseCode(200);
        } else {
            setResponseCode(400);
        }
    }, [searchParams]);

    const fetchWorkstations = async () => {
        if (!currentUser || !clientId || (!conflictDate && !months)) return;

        setLoading(true);
        try {
            const response = await workstationIpConflictService.getWorkstationIpConflictDetails(
                clientId,
                conflictDate,
                months,
                getUserTimezone()
            );
            setWorkstations(response.data);
            setResponseCode(200);
        } catch (error) {
            const errorMessage =
                (error.response && error.response.data) ||
                error.message ||
                error.toString();
            console.log(error);
            setResponseCode(error.response?.status || 500);
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWorkstations();
    }, [clientId, conflictDate, currentUser]);

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    if (!clientId || (!conflictDate && !months)) {
        return (
            <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
                <div className="alert alert-danger" role="alert">
                    Client ID and either Conflict Date (for same-day analysis) or Months (for range analysis) are required.
                </div>
            </div>
        );
    }

    const handleToggleDisable = async (workstationId) => {
        setActionLoading(true);
        try {
            await workstationService.toggleDisableWorkstation(clientId, workstationId);
            toast.success("Workstation status updated successfully");
            // Refresh the list
            await fetchWorkstations();
        } catch (error) {
            const errorMessage = error.response?.data || error.message || "Failed to update workstation status";
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
            console.error("Toggle disable failed:", error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteSingle = async (workstationId) => {
        if (!window.confirm("Are you sure you want to delete this workstation?")) {
            return;
        }

        setActionLoading(true);
        try {
            await workstationService.deleteWorkstation(clientId, workstationId);
            toast.success("Workstation deleted successfully");
            // Refresh the list
            await fetchWorkstations();
        } catch (error) {
            const errorMessage = error.response?.data || error.message || "Failed to delete workstation";
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
            console.error("Delete failed:", error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRowClick = async (workstation) => {
        // Only allow click if there's an IP address
        if (!workstation.ipAddress) {
            toast.info("No audit records available for this workstation on this date");
            return;
        }

        setSelectedWorkstation(workstation.workstationName);
        setShowAuditModal(true);
        setAuditLoading(true);
        setAuditData([]);

        try {
            // Use the specific date from the workstation row if available (important for range view)
            // ensuring we request audit logs for the day this conflict occurred
            let dateToUse = conflictDate;

            if (workstation.conflictDate) {
                // Determine what date to use based on what the user sees
                // The backend returns UTC (e.g. 2026-02-07 01:07)
                // The frontend displays Local (e.g. 2026-02-06 20:07 EST)
                // When the user clicks, they expect to see logs for "2026-02-06"
                // So we must convert the UTC conflictDate to LOCAL YYYY-MM-DD string

                // formatDateOnly returns localized string (e.g. "02/06/2026")
                const localDateStr = formatDateOnly(workstation.conflictDate);

                // Parse it back to YYYY-MM-DD for the API
                const parts = localDateStr.split('/');
                if (parts.length === 3) {
                    const [month, day, year] = parts;
                    dateToUse = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                } else {
                    // Fallback: If locale is weird, use Date object in local time
                    const d = new Date(workstation.conflictDate);
                    const year = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    dateToUse = `${year}-${month}-${day}`;
                }
            }

            const response = await workstationIpConflictService.getWorkstationAuditDetails(
                clientId,
                workstation.workstationName,
                dateToUse,
                getUserTimezone()
            );
            setAuditData(response.data);
        } catch (error) {
            const errorMessage =
                (error.response && error.response.data) ||
                error.message ||
                error.toString();
            console.error("Error fetching audit details:", error);
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
        } finally {
            setAuditLoading(false);
        }
    };

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <h3>Workstation IP Conflict Analysis</h3>
            {clientName && (<h4>{clientName}</h4>)}
            <div className="d-flex mb-2">
                <span className="text-nowrap me-2"><strong>Client ID:</strong></span>
                <span>{clientId}</span>
            </div>
            <div className="d-flex mb-3">
                <span className="text-nowrap me-2"><strong>{conflictDate ? "Conflict Date:" : "Analysis Period:"}</strong></span>
                <span>{conflictDate || (months ? `Last ${months} Month(s)` : "All Time")}</span>
            </div>

            <div className="form-check form-switch mb-4">
                <input
                    className="form-check-input"
                    type="checkbox"
                    id="includeNonConflicting"
                    checked={includeNonConflicting}
                    onChange={(e) => setIncludeNonConflicting(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="includeNonConflicting">
                    Include non-conflicting workstations
                </label>
            </div>

            {!loading && responseCode >= 400 && responseCode !== 200 && (
                <div className="alert alert-danger mb-4" role="alert">
                    {responseCode === 401
                        ? "Not authorized to access workstation conflict details"
                        : responseCode === 404
                            ? "Client not found"
                            : "An error occurred while loading workstation conflict details"
                    }
                </div>
            )}

            {/* Workstation Table */}
            <div className="row mt-3">
                <div className="col-12">
                    <table className="table table-striped table-hover">
                            <thead className="table-dark">
                                <tr>
                                    <th scope="col">ID</th>
                                    <th scope="col">Name</th>
                                    <th scope="col">Location</th>
                                    <th scope="col">Created</th>
                                    <th scope="col">Updated</th>
                                    <th scope="col">Conflict Date</th>
                                    <th scope="col">IP Address</th>
                                    <th scope="col">Subscription</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {workstations && workstations.length > 0 ? (
                                    workstations.filter(w => includeNonConflicting || w.ipAddress).map((workstation) => (
                                        <tr
                                            key={`${workstation.workstationId}-${workstation.ipAddress || 'no-ip'}`}
                                            onClick={() => handleRowClick(workstation)}
                                            style={{
                                                cursor: workstation.ipAddress ? 'pointer' : 'default'
                                            }}
                                            title={workstation.ipAddress ? 'Click to view audit details' : ''}
                                        >
                                            <td>{workstation.workstationId}</td>
                                            <td>{workstation.workstationName}</td>
                                            <td>{workstation.location || <span className="text-muted">—</span>}</td>
                                            <td>{formatDateTimeWithZone(workstation.createdAt)}</td>
                                            <td>{formatDateTimeWithZone(workstation.updatedAt)}</td>
                                            <td>{formatDateTimeWithZone(workstation.conflictDate)}</td>
                                            <td>
                                                {workstation.ipAddress ? (
                                                    <span className="badge bg-primary">{workstation.ipAddress}</span>
                                                ) : (
                                                    <span className="text-muted">No IP on this date</span>
                                                )}
                                            </td>
                                            <td>{workstation.subscription || 'No subscription'}</td>
                                            <td>
                                                {workstation.disabled ? (
                                                    <span className="badge bg-danger">Disabled</span>
                                                ) : (
                                                    <span className="badge bg-success">Active</span>
                                                )}
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="d-flex gap-2">
                                                    <button
                                                        onClick={() => handleToggleDisable(workstation.workstationId)}
                                                        disabled={actionLoading}
                                                        className={`btn btn-sm ${workstation.disabled ? 'btn-success' : 'btn-warning'}`}
                                                        title={workstation.disabled ? 'Enable workstation' : 'Disable workstation'}
                                                    >
                                                        {workstation.disabled ? 'Enable' : 'Disable'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteSingle(workstation.workstationId)}
                                                        disabled={actionLoading}
                                                        className="btn btn-sm btn-outline-danger"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="text-center">
                                            {loading ? 'Loading workstations...' : 'No workstations found'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            {/* Audit Details Modal */}
            <WorkstationAuditModal
                show={showAuditModal}
                onHide={() => setShowAuditModal(false)}
                auditData={auditData}
                workstation={selectedWorkstation}
                loading={auditLoading}
            />


        </div>
    );
};

export default WorkstationIpConflictView;
